import React, { useEffect, useState } from 'react'
import { Button } from 'reactstrap'
import useAsyncService from '../../../../hooks/useAsyncService'
import ICSErrorCard from '../../../../components/ICSErrorCard/ICSErrorCard'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { useDispatch } from 'react-redux'
import { dataSlice, DataStateVersions } from '../../../../redux/slices'
import { Container, Row, Col } from 'reactstrap'
import RenderDataModal from '../RenderViewModal/RenderViewModal'
import { LoadIndicator } from 'devextreme-react'
import { ShowDataGrid } from '../SelectedFilesGrid/ShowDataGrid'
import './ExportSelectionModal.css'

import {
  getUploadExportFormats,
  exportVSB,
  ExportFormatsResponseType
} from '../../../../services/endpoints/data'
interface SelectedFile {
  id: string
  displayName: string
  startDate: string
  endDate: string
  extension: string
  vehicle: { name: string, displayName: string }
}
interface ExportSelectionsModalProps {
  onClose: () => void
}

interface SelectedIdsMap {
  [deviceId: number]: number[]
}

export const ExportSelectionModal: React.FC<ExportSelectionsModalProps> = ({
  onClose
}) => {
  const versions = useAppSelector((State) => State.data.versions)
  const [exportStarted, setExportStarted] = useState(false)
  const [forceRate, setForceRate] = useState<number>(-1)
  const [format, setFormat] = useState<number>(0)
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])
  const [selectedIds, setSelectedIds] = useState<SelectedIdsMap | null>(null)

  const { serviceLoading, serviceValue, serviceError, setServiceArgs } =
    useAsyncService({
      serviceCall: getUploadExportFormats,
      delay: 0,
      initialArgs: null
    })

  useEffect(() => {
    if (selectedFiles.length === 0) {
      return
    }
    setServiceArgs('name')
  }, [selectedFiles])

  useEffect(() => {
    const { selectedFiles, selectedIds } = extractSelectedFilesAndIds(versions)
    setSelectedFiles(selectedFiles)
    setSelectedIds(selectedIds)
  }, [versions])

  const getBody = () => {
    if (!selectedIds) {
      return <_ShowNoExportableFiles />
    } else if (serviceValue && !exportStarted) {
      return (
        <>
          <ShowDataGrid selectedFiles={selectedFiles} />
          <_ShowExportFormats
            availableExports={serviceValue}
            forceRate={forceRate}
            setForceRate={setForceRate}
            format={format}
            setFormat={setFormat}
          />
        </>
      )
    } else if (exportStarted) {
      return (
        <_ShowExportResults
          selectedIds={selectedIds}
          forceRate={forceRate}
          format={format}
        />
      )
    }
    return <LoadIndicator />
  }

  return (
    <RenderDataModal
      onClose={onClose}
      title={exportStarted ? 'Export Results' : 'Selected Files'}
      additionalButtons={
        !exportStarted && selectedIds
          ? [
              <_createExportButton
                key="export-button"
                selectedFiles={selectedFiles}
                setExporting={setExportStarted}
              />,
              <_createClearAllButton
                key="clear-button"
                selectedFiles={selectedFiles}
              />
            ]
          : []
      }
    >
      <Container className="create-export-container">
        <Row className="create-export-">
          <Col>{getBody()}</Col>
        </Row>
      </Container>
    </RenderDataModal>
  )
}

const _createExportButton: React.FC<{
  selectedFiles: SelectedFile[]
  setExporting: (value: boolean) => void
}> = ({ selectedFiles, setExporting }) => {
  const handleExportSelections = () => {
    setExporting(true)
  }
  return (
    <div>
      <Button
        className="modal-btn close-btn"
        onClick={handleExportSelections}
        disabled={selectedFiles.length == 0}
      >
        <span className="glyphicons glyphicons-restart"></span>
        {'Export'}
      </Button>
    </div>
  )
}

const _ShowNoExportableFiles: React.FC = () => {
  return (
    <span className="export-alert alert-warning">
      No exportable files were found. A VSDB is required. Any files selected
      that were not exportable have been unselected. Please select the VSB.
    </span>
  )
}

const _ShowExportResults: React.FC<{
  selectedIds: SelectedIdsMap
  forceRate: number
  format: number
}> = ({ selectedIds, forceRate, format }) => {
  const { serviceLoading, serviceValue, serviceError, setServiceArgs } =
    useAsyncService({
      serviceCall: exportVSB,
      delay: 500,
      initialArgs: null
    })

  useEffect(() => {
    setServiceArgs({ selectedIds, forceRate, format })
  }, [])

  if (serviceError) {
    return <ICSErrorCard code={'error'} message={serviceError} fields={[]} />
  } else if (serviceValue) {
    return (
      <div className="success-wrapper">
        <div className="success-detail">
          <p>Your export has been scheduled to be processed.</p>
          <h4 className="success-heading">Success</h4>
        </div>
        <div className="success-report">
          {serviceValue.success.map((item) => {
            return (
              <div className="sucess-report-row" key={item.device}>
                <div className="report-item">
                  <span className="glyphicons glyphicons-hdd"></span>
                  <span className="report-detail">
                    {' '}
                    Device ID: {item.device}{' '}
                  </span>
                </div>
                <span className="report-detail">-</span>
                <div className="report-item">
                  <span className="glyphicons glyphicons-factory"></span>
                  <span className="report-detail"> Jobs: {item.jobs} </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
  return <LoadIndicator />
}

const _ShowExportFormats: React.FC<{
  availableExports: ExportFormatsResponseType[]
  forceRate: number
  format: number
  setForceRate: React.Dispatch<React.SetStateAction<number>>
  setFormat: React.Dispatch<React.SetStateAction<number>>
}> = ({ availableExports, forceRate, setForceRate, setFormat }) => {
  const [selectedExports, setSelectedExports] = useState<number[]>([])

  const handleSelect = (exp: ExportFormatsResponseType) => {
    setSelectedExports((prevSelected) => {
      if (prevSelected.includes(exp.id)) {
        return prevSelected.filter((id) => id !== exp.id) // Deselect
      } else {
        return [...prevSelected, exp.id] // Select
      }
    })
  }

  useEffect(() => {
    const selectedSum = availableExports
      .filter((exp) => selectedExports.includes(exp.id))
      .reduce((sum, exp) => sum + exp.exporterValue, 0)

    setFormat(selectedSum)
  }, [selectedExports, availableExports])

  return (
    <>
      <h5 className="modal-subheading">Available Exports</h5>
      <Row className="exports-btns-row">
        {availableExports.length > 0 &&
          availableExports
            .filter((exp) => exp.enabled)
            .map((exp) => (
              <Button
                key={exp.id}
                className={`avail-expo-btn ${
                  selectedExports.includes(exp.id) ? 'active' : 'disabled'
                }`}
                onClick={() => handleSelect(exp)}
              >
                {exp.name} (.{exp.extension})
              </Button>
            ))}
      </Row>
      <div className="forcerate-row">
        <label>Force Rate</label>
        <input
          type="number"
          value={forceRate}
          onChange={(e) => {
            const value = Number(e.target.value)
            setForceRate(Math.min(1000, Math.max(-1, value)))
          }}
        />
      </div>
    </>
  )
}

const _createClearAllButton: React.FC<{ selectedFiles: SelectedFile[] }> = ({
  selectedFiles
}) => {
  const dispatch = useDispatch()
  const clearAllSelections = () => {
    dispatch(dataSlice.actions.clearAllSelections())
  }

  return (
    <div>
      <Button
        className="modal-btn clear-btn"
        onClick={clearAllSelections}
        disabled={selectedFiles.length == 0}
      >
        <span className="glyphicons glyphicons-ban-circle"></span> Clear
        Selections
      </Button>
    </div>
  )
}

const extractSelectedFilesAndIds = (
  versions: DataStateVersions
): {
  selectedFiles: SelectedFile[]
  selectedIds: SelectedIdsMap | null
} => {
  return Object.entries(versions).reduce<{
    selectedFiles: SelectedFile[]
    selectedIds: SelectedIdsMap | null
  }>(
    (acc, [id, version]) => {
      if (version?.selected) {
        acc.selectedFiles.push({
          id, // Extract the key as ID
          displayName: version.fileName,
          startDate: version.startDate,
          endDate: version.endDate,
          extension: version.type,
          vehicle: { name: version.vehicleName, displayName: version.vehicleDisplayName }
        })

        if (version.type === 'vsb') {
          // Initialize selectedIds if it's null
          if (!acc.selectedIds) {
            acc.selectedIds = {}
          }
          // Ensure the array exists before pushing
          if (!acc.selectedIds[version.deviceId]) {
            acc.selectedIds[version.deviceId] = []
          }
          acc.selectedIds[version.deviceId].push(parseInt(id))
        }
      }
      return acc
    },
    { selectedFiles: [], selectedIds: null }
  )
}
export default ExportSelectionModal
